import { AsyncPipe } from '@angular/common';
import type { OnInit, Signal } from '@angular/core';
import { Component, Injector, effect, inject, input } from '@angular/core';
import { toSignal } from '@angular/core/rxjs-interop';
import {
  NonNullableFormBuilder,
  ReactiveFormsModule,
  Validators,
} from '@angular/forms';
import { TranslatePipe } from '@ngx-translate/core';
import type { OnyxDate, OnyxSuggestion } from '@onyx/angular';
import {
  OnyxDatepickerComponent,
  OnyxDropdownComponent,
  OnyxFormGroupComponent,
  OnyxSectionComponent,
  OnyxSuggestionsComponent,
  OnyxTextFieldComponent,
  OnyxUploadComponent,
  onyxDateValidator,
  onyxTextFieldLengthValidator,
} from '@onyx/angular';
import { difference } from 'lodash';
import type { Country } from '../../../../common/enums/country';
import { DictionaryCode } from '../../../../common/enums/dictionary-code';
import { ValidationHelper } from '../../../../common/helpers/validation.helper';
import { DictionariesService } from '../../../../common/services/dictionaries.service';
import { DriversLicenseCategory } from '../../common/enums/drivers-license-category';

type DriverDriversLicenseFormGroup = ReturnType<
  typeof DriverDriversLicenseFormComponent.buildForm
>;

type DriverDriversLicenseCategoryFormGroup = ReturnType<
  typeof DriverDriversLicenseFormComponent.buildCategoryForm
>;

@Component({
  selector: 'app-driver-drivers-license-form',
  imports: [
    AsyncPipe,
    OnyxSectionComponent,
    TranslatePipe,
    ReactiveFormsModule,
    OnyxDropdownComponent,
    OnyxDatepickerComponent,
    OnyxTextFieldComponent,
    OnyxUploadComponent,
    OnyxFormGroupComponent,
    OnyxSuggestionsComponent,
  ],
  templateUrl: './driver-drivers-license-form.component.html',
  styleUrl: './driver-drivers-license-form.component.scss',
})
export class DriverDriversLicenseFormComponent implements OnInit {
  private readonly dictionariesService = inject(DictionariesService);
  private readonly injector = inject(Injector);
  private readonly fb = inject(NonNullableFormBuilder);

  public readonly form = input.required<DriverDriversLicenseFormGroup>();

  protected readonly countries$ = this.dictionariesService.getDictionary(
    DictionaryCode.COUNTRY,
  );

  protected readonly I18N =
    'drivers.driverForm.driversLicenseAndProfessionalQualifications';
  protected readonly CATEGORY_SUGGESTIONS: OnyxSuggestion<string>[] = [
    DriversLicenseCategory.B,
    DriversLicenseCategory.C,
    DriversLicenseCategory.CE,
  ].map((value) => ({
    name: `${DictionaryCode.DRIVERS_LICENSE_CATEGORY}.${value}`,
    value,
  }));

  protected readonly DictionaryCode = DictionaryCode;

  protected readonly categories = toSignal(
    this.dictionariesService.getDictionary(
      DictionaryCode.DRIVERS_LICENSE_CATEGORY,
    ),
  );

  protected selectedCategories!: Signal<string[]>;

  protected get categoriesControl() {
    return this.form().controls.categories;
  }
  protected get professionalQualificationsExpirationDateControl() {
    return this.form().controls.professionalQualificationsExpirationDate;
  }

  public ngOnInit(): void {
    const selectedCategoriesControl = this.form().controls.selectedCategories;
    this.selectedCategories = toSignal(selectedCategoriesControl.valueChanges, {
      initialValue: selectedCategoriesControl.getRawValue(),
      injector: this.injector,
    });

    effect(
      () => {
        const selectedCategories = this.selectedCategories();
        const activeCategories = this.categoriesControl
          .getRawValue()
          .map((group) => group.category);

        const removedCategories = difference(
          activeCategories,
          selectedCategories,
        );
        for (const category of removedCategories) {
          const index = this.categoriesControl
            .getRawValue()
            .findIndex((group) => group.category === category);
          this.categoriesControl.removeAt(index);
        }

        const addedCategories = difference(
          selectedCategories,
          activeCategories,
        );
        for (const category of addedCategories) {
          this.categoriesControl.insert(
            selectedCategories.indexOf(category),
            DriverDriversLicenseFormComponent.buildCategoryForm(
              this.fb,
              category,
            ),
          );
        }

        if (this.categories()) {
          const hasProfessionalQualifications = selectedCategories.some(
            (category) =>
              this.categories()?.find((c) => c.value === category)
                ?.requireProfessionalQualifications,
          );
          ValidationHelper.toggleControls(
            this.professionalQualificationsExpirationDateControl,
            { enabled: hasProfessionalQualifications, reset: true },
          );
        }
      },
      { injector: this.injector },
    );
  }

  public static buildForm(fb: NonNullableFormBuilder) {
    return fb.group({
      selectedCategories: fb.control<string[]>([], [Validators.required]),
      categories: fb.array<DriverDriversLicenseCategoryFormGroup>(
        [],
        [Validators.required],
      ),
      professionalQualificationsExpirationDate: fb.control<string | null>(
        {
          value: null,
          disabled: true,
        },
        [Validators.required, onyxDateValidator],
      ),
      driversLicenseNumber: fb.control<string | null>(null, [
        Validators.required,
        onyxTextFieldLengthValidator,
      ]),
      driversLicenseIssuingCountry: fb.control<Country | null>(null),
      scans: fb.control<string[]>([]),
    });
  }

  public static buildCategoryForm(
    fb: NonNullableFormBuilder,
    category: string,
  ) {
    return fb.group({
      category: fb.control(
        {
          value: category,
          disabled: true,
        },
        [Validators.required],
      ),
      expirationDate: fb.control<OnyxDate | null>(null, [
        Validators.required,
        onyxDateValidator,
      ]),
    });
  }
}
